import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import type { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { Copy, Loader2, Mail, Lock, Bell, Globe, User as UserIcon, Link2, Eye, EyeOff, AlertTriangle, CreditCard } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { evaluatePasswordStrength } from "@/lib/password-strength";
import { cn } from "@/lib/utils";
import { deleteMyAccount } from "@/lib/account.functions";

export const Route = createFileRoute("/_authenticated/settings")({
  head: () => ({
    meta: [
      { title: "設定 - KAGEM" },
      { name: "description", content: "アカウント設定、通知、パスワード、言語などを管理" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SettingsPage,
});

type NotificationSettings = {
  completion: boolean;
  news: boolean;
  promo: boolean;
};

type ProfileRow = {
  display_name: string | null;
  avatar_url: string | null;
  referral_code: string | null;
  phone_number: string | null;
  phone_verified: boolean | null;
  notification_settings: NotificationSettings | null;
  locale: string | null;
};

const DEFAULT_NOTIFICATIONS: NotificationSettings = {
  completion: true,
  news: true,
  promo: false,
};

function SettingsPage() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileRow | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      const { data } = await supabase.auth.getUser();
      if (!active) return;
      const u = data.user ?? null;
      setUser(u);
      // ユーザーが取れたらすぐ表示し、プロフィールは裏で取得（待たない）
      setLoading(false);
      if (u) {
        const { data: prof } = await supabase
          .from("profiles")
          .select("display_name, avatar_url, referral_code, phone_number, phone_verified, notification_settings, locale")
          .eq("user_id", u.id)
          .maybeSingle();
        if (!active) return;
        setProfile((prof as ProfileRow | null) ?? null);
      }
    })();
    return () => {
      active = false;
    };
  }, []);


  return (
    <div className="px-4 py-8 md:px-6 md:py-10">
      <div className="mx-auto max-w-4xl">
          <header className="mb-6">
            <h1 className="text-3xl font-bold tracking-tight">設定</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              アカウント情報・通知・セキュリティを管理します
            </p>
          </header>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              読み込み中...
            </div>
          ) : !user ? (
            <p className="text-muted-foreground">ログインしてください。</p>
          ) : (
            <Tabs defaultValue="profile" className="w-full">
              <TabsList className="grid h-auto w-full grid-cols-3 md:grid-cols-7">
                <TabsTrigger value="profile" className="gap-1.5">
                  <UserIcon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">プロフィール</span>
                </TabsTrigger>
                <TabsTrigger value="email" className="gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">メール</span>
                </TabsTrigger>
                <TabsTrigger value="password" className="gap-1.5">
                  <Lock className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">パスワード</span>
                </TabsTrigger>
                <TabsTrigger value="subscription" className="gap-1.5">
                  <CreditCard className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">プラン</span>
                </TabsTrigger>
                <TabsTrigger value="language" className="gap-1.5">
                  <Globe className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">言語</span>
                </TabsTrigger>
                <TabsTrigger value="connections" className="gap-1.5">
                  <Link2 className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">連携</span>
                </TabsTrigger>
                <TabsTrigger value="account" className="gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">アカウント</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="mt-6">
                <ProfileSection user={user} profile={profile} onUpdate={setProfile} />
              </TabsContent>
              <TabsContent value="email" className="mt-6 space-y-6">
                <EmailSection user={user} />
                <NotificationSection user={user} profile={profile} onUpdate={setProfile} />
              </TabsContent>
              <TabsContent value="password" className="mt-6">
                <PasswordSection />
              </TabsContent>
              <TabsContent value="subscription" className="mt-6">
                <SubscriptionSection user={user} />
              </TabsContent>
              <TabsContent value="language" className="mt-6">
                <LanguageSection profile={profile} />
              </TabsContent>
              <TabsContent value="connections" className="mt-6">
                <ConnectionsSection user={user} />
              </TabsContent>
              <TabsContent value="account" className="mt-6">
                <DangerZoneSection />
              </TabsContent>
            </Tabs>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 1. Profile                                                                 */
/* -------------------------------------------------------------------------- */

function ProfileSection({
  user,
  profile,
  onUpdate,
}: {
  user: User;
  profile: ProfileRow | null;
  onUpdate: (p: ProfileRow | null) => void;
}) {
  const [displayName, setDisplayName] = useState(profile?.display_name ?? "");
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url ?? null);
  const [savingName, setSavingName] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const initialName = profile?.display_name ?? "";
  const dirty = displayName.trim() !== initialName.trim();

  const initials = (displayName || user.email || "?").slice(0, 2).toUpperCase();

  const handleSaveName = async () => {
    const trimmed = displayName.trim();
    if (trimmed.length === 0) {
      toast.error("表示名を入力してください");
      return;
    }
    if (trimmed.length > 30) {
      toast.error("表示名は30文字以内で入力してください");
      return;
    }
    setSavingName(true);
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: trimmed })
      .eq("user_id", user.id);
    setSavingName(false);
    if (error) {
      toast.error("保存に失敗しました");
      return;
    }
    toast.success("表示名を更新しました");
    onUpdate(profile ? { ...profile, display_name: trimmed } : null);
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("JPG または PNG ファイルを選択してください");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("ファイルサイズは2MB以下にしてください");
      return;
    }
    setUploading(true);
    try {
      const ext = file.type === "image/png" ? "png" : "jpg";
      const path = `${user.id}/avatar.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("uploads")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;

      // Create a long-lived signed URL (1 year)
      const { data: signed, error: signErr } = await supabase.storage
        .from("uploads")
        .createSignedUrl(path, 60 * 60 * 24 * 365);
      if (signErr || !signed) throw signErr ?? new Error("Signed URL failed");

      const { error: profErr } = await supabase
        .from("profiles")
        .update({ avatar_url: signed.signedUrl })
        .eq("user_id", user.id);
      if (profErr) throw profErr;

      setAvatarUrl(signed.signedUrl);
      onUpdate(profile ? { ...profile, avatar_url: signed.signedUrl } : null);
      toast.success("アバターを更新しました");
    } catch (err) {
      console.error(err);
      toast.error("アバターのアップロードに失敗しました");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleCopyReferral = async () => {
    if (!profile?.referral_code) return;
    try {
      await navigator.clipboard.writeText(profile.referral_code);
      toast.success("紹介コードをコピーしました");
    } catch {
      toast.error("コピーに失敗しました");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>プロフィール</CardTitle>
        <CardDescription>表示名、アバター、紹介コードなどを管理します</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Avatar */}
        <div className="flex items-center gap-4">
          <Avatar className="h-20 w-20">
            {avatarUrl ? <AvatarImage src={avatarUrl} alt={displayName} /> : null}
            <AvatarFallback className="bg-primary/15 text-lg text-primary">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png"
              className="hidden"
              onChange={handleAvatarChange}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {uploading && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              画像を変更
            </Button>
            <p className="mt-1.5 text-xs text-muted-foreground">JPG/PNG、最大2MB</p>
          </div>
        </div>

        {/* Display name */}
        <div className="space-y-2">
          <Label htmlFor="display-name">表示名</Label>
          <div className="flex items-end gap-2">
            <Input
              id="display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={30}
              placeholder="表示名（最大30文字）"
            />
            <Button onClick={handleSaveName} disabled={!dirty || savingName}>
              {savingName && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              保存
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{displayName.length}/30</p>
        </div>

        {/* Referral code */}
        <div className="space-y-2">
          <Label>紹介コード</Label>
          <div className="flex gap-2">
            <Input value={profile?.referral_code ?? "—"} readOnly className="font-mono" />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleCopyReferral}
              disabled={!profile?.referral_code}
              aria-label="紹介コードをコピー"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* 2a. Email change                                                           */
/* -------------------------------------------------------------------------- */

function EmailSection({ user }: { user: User }) {
  const [newEmail, setNewEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const dirty = newEmail.trim().length > 0 && newEmail.trim() !== user.email;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("有効なメールアドレスを入力してください");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ email: trimmed });
    setSubmitting(false);
    if (error) {
      toast.error(error.message ?? "メール変更に失敗しました");
      return;
    }
    toast.success("確認メールを送信しました。新しいメールアドレスから認証してください。");
    setNewEmail("");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>メールアドレス</CardTitle>
        <CardDescription>登録メールアドレスの変更</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label>現在のメールアドレス</Label>
          <Input value={user.email ?? ""} readOnly />
        </div>
        <form onSubmit={handleSubmit} className="space-y-2">
          <Label htmlFor="new-email">新しいメールアドレス</Label>
          <div className="flex gap-2">
            <Input
              id="new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="new@example.com"
              autoComplete="email"
            />
            <Button type="submit" disabled={!dirty || submitting}>
              {submitting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              変更
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            変更後、新しいアドレスへ確認メールが送信されます。
          </p>
        </form>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* 2b. Notifications                                                          */
/* -------------------------------------------------------------------------- */

function NotificationSection({
  user,
  profile,
  onUpdate,
}: {
  user: User;
  profile: ProfileRow | null;
  onUpdate: (p: ProfileRow | null) => void;
}) {
  const settings = profile?.notification_settings ?? DEFAULT_NOTIFICATIONS;

  const updateSetting = async (key: keyof NotificationSettings, value: boolean) => {
    const next: NotificationSettings = { ...settings, [key]: value };
    // Optimistic
    onUpdate(profile ? { ...profile, notification_settings: next } : null);
    const { error } = await supabase
      .from("profiles")
      .update({ notification_settings: next })
      .eq("user_id", user.id);
    if (error) {
      toast.error("通知設定の保存に失敗しました");
      // Revert
      onUpdate(profile ? { ...profile, notification_settings: settings } : null);
    } else {
      toast.success("通知設定を更新しました");
    }
  };

  const items: { key: keyof NotificationSettings; title: string; desc: string }[] = [
    { key: "completion", title: "生成完了通知", desc: "動画生成が完了したときにメールで通知" },
    { key: "news", title: "お知らせメール", desc: "サービスの重要なお知らせやアップデート" },
    { key: "promo", title: "プロモーションメール", desc: "キャンペーンやお得な情報" },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-4 w-4" />
          通知設定
        </CardTitle>
        <CardDescription>受信するメール通知の種類を選択します</CardDescription>
      </CardHeader>
      <CardContent className="space-y-1">
        {items.map((item) => (
          <div
            key={item.key}
            className="flex items-center justify-between gap-4 border-b border-border/60 py-3 last:border-0"
          >
            <div>
              <p className="text-sm font-medium">{item.title}</p>
              <p className="text-xs text-muted-foreground">{item.desc}</p>
            </div>
            <Switch
              checked={settings[item.key]}
              onCheckedChange={(v) => updateSetting(item.key, v)}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* 3. Password change                                                         */
/* -------------------------------------------------------------------------- */

function PasswordSection() {
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const strength = useMemo(() => evaluatePasswordStrength(newPwd), [newPwd]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPwd.length < 8) {
      toast.error("新しいパスワードは8文字以上で入力してください");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("確認用パスワードが一致しません");
      return;
    }
    setSubmitting(true);

    // Re-authenticate by signing in with current password to verify
    const { data: userData } = await supabase.auth.getUser();
    const email = userData.user?.email;
    if (!email) {
      setSubmitting(false);
      toast.error("ユーザー情報を取得できませんでした");
      return;
    }
    const { error: signInErr } = await supabase.auth.signInWithPassword({
      email,
      password: currentPwd,
    });
    if (signInErr) {
      setSubmitting(false);
      toast.error("現在のパスワードが正しくありません");
      return;
    }

    const { error } = await supabase.auth.updateUser({ password: newPwd });
    setSubmitting(false);
    if (error) {
      toast.error(error.message ?? "パスワード変更に失敗しました");
      return;
    }
    toast.success("パスワードを変更しました");
    setCurrentPwd("");
    setNewPwd("");
    setConfirmPwd("");
  };

  const strengthColor =
    strength.level === "strong"
      ? "bg-emerald-500"
      : strength.level === "medium"
        ? "bg-amber-500"
        : "bg-destructive";

  return (
    <Card>
      <CardHeader>
        <CardTitle>パスワード変更</CardTitle>
        <CardDescription>セキュリティのため、定期的に変更することを推奨します</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="current-pwd">現在のパスワード</Label>
            <div className="relative">
              <Input
                id="current-pwd"
                type={showCurrent ? "text" : "password"}
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                aria-label="表示切り替え"
              >
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-pwd">新しいパスワード</Label>
            <div className="relative">
              <Input
                id="new-pwd"
                type={showNew ? "text" : "password"}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                aria-label="表示切り替え"
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {newPwd && (
              <div className="flex items-center gap-2 pt-1">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn("h-full transition-all", strengthColor)}
                    style={{ width: `${(strength.score / 3) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground">{strength.label}</span>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm-pwd">新しいパスワード（確認）</Label>
            <Input
              id="confirm-pwd"
              type={showNew ? "text" : "password"}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              autoComplete="new-password"
              required
            />
            {confirmPwd && confirmPwd !== newPwd && (
              <p className="text-xs text-destructive">パスワードが一致しません</p>
            )}
          </div>

          <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
            {submitting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            パスワードを変更
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* 4. Language                                                                */
/* -------------------------------------------------------------------------- */

function LanguageSection({ profile }: { profile: ProfileRow | null }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>言語設定</CardTitle>
        <CardDescription>表示言語を選択します</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label>言語</Label>
          <Select value={profile?.locale ?? "ja"} disabled>
            <SelectTrigger className="max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ja">日本語</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground">
          他の言語をご希望の方は
          <a href="/contact" className="ml-1 underline hover:text-foreground">
            お問い合わせ
          </a>
          ください。
        </p>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* 5. Connected accounts                                                      */
/* -------------------------------------------------------------------------- */

function ConnectionsSection({ user }: { user: User }) {
  const identities = user.identities ?? [];
  const providers = new Set(identities.map((i) => i.provider));
  const hasGoogle = providers.has("google");
  const hasEmail = providers.has("email");

  return (
    <Card>
      <CardHeader>
        <CardTitle>接続済みアカウント</CardTitle>
        <CardDescription>外部サービスとの連携状況</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ProviderRow
          name="メール / パスワード"
          connected={hasEmail}
        />
        <ProviderRow
          name="Google"
          connected={hasGoogle}
        />
        <p className="pt-2 text-xs text-muted-foreground">
          ※ 連携の追加・解除は今後のアップデートで対応予定です。
        </p>
      </CardContent>
    </Card>
  );
}

function ProviderRow({ name, connected }: { name: string; connected: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-card px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Link2 className="h-4 w-4" />
        </div>
        <span className="text-sm font-medium">{name}</span>
      </div>
      {connected ? (
        <Badge variant="secondary" className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
          連携済み
        </Badge>
      ) : (
        <Badge variant="outline">未連携</Badge>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 6. Danger Zone — Delete account                                            */
/* -------------------------------------------------------------------------- */

function DangerZoneSection() {
  const navigate = useNavigate();
  const deleteAccountFn = useServerFn(deleteMyAccount);
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canDelete = confirmText === "DELETE";

  const handleDelete = async () => {
    if (!canDelete) return;
    setSubmitting(true);
    try {
      await deleteAccountFn({ data: { confirmation: "DELETE" } });
      toast.success("アカウントを削除しました");
      await supabase.auth.signOut();
      setOpen(false);
      void navigate({ to: "/" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "アカウントの削除に失敗しました";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-4 w-4" />
          アカウントの削除
        </CardTitle>
        <CardDescription>
          この操作は取り消せません。アカウント・プロフィール・生成履歴・クレジット・サブスクリプション情報など、すべてのデータが完全に削除されます。
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-muted-foreground">
          <p className="mb-2 font-medium text-foreground">削除前にご確認ください</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>保有クレジット・サブスクリプションは失効し、返金されません。</li>
            <li>有料プランをご利用中の場合は、先に
              <a href="/billing" className="ml-1 underline hover:text-foreground">解約</a>
              をお願いします。</li>
            <li>削除後に同じメールアドレスで再登録は可能ですが、過去のデータは復元できません。</li>
          </ul>
        </div>

        <AlertDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirmText(""); }}>
          <AlertDialogTrigger asChild>
            <Button variant="destructive">アカウントを削除する</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>本当にアカウントを削除しますか？</AlertDialogTitle>
              <AlertDialogDescription>
                この操作は取り消せません。続行するには、下のボックスに <span className="font-mono font-semibold text-foreground">DELETE</span> と入力してください。
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2">
              <Label htmlFor="delete-confirm">確認</Label>
              <Input
                id="delete-confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={submitting}>キャンセル</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); void handleDelete(); }}
                disabled={!canDelete || submitting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {submitting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                完全に削除する
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* 7. Subscription — Cancel                                                   */
/* -------------------------------------------------------------------------- */

type SubscriptionRow = {
  stripe_subscription_id: string | null;
  plan: string | null;
  status: string | null;
  contract_type: string | null;
  minimum_term_end: string | null;
  current_period_end: string | null;
};

const PLAN_LABEL_JA: Record<string, string> = {
  light: "Light",
  standard: "Standard",
  pro: "Pro",
};

const PLAN_PRICE_JA: Record<string, string> = {
  light: "¥2,980 / 月",
  standard: "¥8,800 / 月",
  pro: "¥24,800 / 月",
};

function formatDateJa(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

function SubscriptionSection({ user }: { user: User }) {
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState<SubscriptionRow | null>(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("subscriptions")
      .select("stripe_subscription_id, plan, status, contract_type, minimum_term_end, current_period_end")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setSub((data as SubscriptionRow | null) ?? null);
    setLoading(false);
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const hasActiveSub =
    sub &&
    sub.stripe_subscription_id &&
    sub.status !== "canceled" &&
    sub.plan &&
    sub.plan !== "free";

  const now = Date.now();
  const minTermMs = sub?.minimum_term_end ? new Date(sub.minimum_term_end).getTime() : 0;
  const isAnnualInTerm =
    sub?.contract_type === "annual" && minTermMs > now;

  const scheduledCancelDate = isAnnualInTerm
    ? sub!.minimum_term_end
    : sub?.current_period_end ?? null;

  const contractLabel =
    sub?.contract_type === "annual" ? "年契約（12ヶ月縛り / 月払い）" : "月契約";

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) {
        toast.error("セッションが失効しています。再度ログインしてください。");
        return;
      }
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: "{}",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = body?.error ?? "UNKNOWN";
        toast.error(`解約に失敗しました（${code}）`);
        return;
      }
      toast.success(body?.message ?? "解約手続きが完了しました");
      setOpen(false);
      await reload();
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "解約に失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-4 w-4" />
          サブスクリプション
        </CardTitle>
        <CardDescription>現在のプランと解約手続き</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            読み込み中...
          </div>
        ) : !hasActiveSub ? (
          <div className="rounded-md border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground">
            <p className="mb-1 font-medium text-foreground">サブスクリプション未加入</p>
            <p>
              有料プランに加入していません。プランへの加入は
              <a href="/pricing" className="ml-1 underline hover:text-foreground">料金プラン</a>
              からどうぞ。
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-md border border-border/60 bg-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">現在のプラン</p>
                  <p className="text-lg font-semibold">
                    {PLAN_LABEL_JA[sub!.plan!] ?? sub!.plan} {" "}
                    <span className="text-sm font-normal text-muted-foreground">
                      {PLAN_PRICE_JA[sub!.plan!] ?? ""}
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">{contractLabel}</p>
                </div>
                <Badge variant={sub!.status === "active" ? "secondary" : "outline"}>
                  {sub!.status ?? "-"}
                </Badge>
              </div>
              {sub!.current_period_end && (
                <p className="mt-3 text-xs text-muted-foreground">
                  次回課金日：{formatDateJa(sub!.current_period_end)}
                </p>
              )}
              {isAnnualInTerm && sub!.minimum_term_end && (
                <p className="mt-1 text-xs text-muted-foreground">
                  年契約 最低契約期間終了日：{formatDateJa(sub!.minimum_term_end)}
                </p>
              )}
            </div>

            <AlertDialog open={open} onOpenChange={setOpen}>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-destructive hover:text-destructive">
                  解約する
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>サブスクリプションを解約しますか？</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      {isAnnualInTerm ? (
                        <p>
                          年契約（12ヶ月縛り）により、
                          <span className="font-semibold text-foreground">
                            {formatDateJa(sub!.minimum_term_end)}
                          </span>
                          まで請求が継続します。その翌日以降に自動的に解約されます。
                        </p>
                      ) : (
                        <p>
                          次回課金日{" "}
                          <span className="font-semibold text-foreground">
                            {formatDateJa(sub!.current_period_end)}
                          </span>{" "}
                          をもって解約されます。それまでは引き続きご利用いただけます。
                        </p>
                      )}
                      <p>解約後、日割り返金は行っておりません。</p>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={submitting}>キャンセル</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.preventDefault();
                      void handleCancel();
                    }}
                    disabled={submitting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {submitting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                    解約する
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {scheduledCancelDate && sub!.status === "canceled" && (
              <p className="text-xs text-muted-foreground">
                ※ 解約予定日：{formatDateJa(scheduledCancelDate)}（Stripe側の反映まで数分かかる場合があります）
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

